/*
 * Copyright (C) Fraunhofer IESE 2021-2025 - Mher Ter-Tovmasyan
 *
 * SPDX-License-Identifier: AGPL-3.0-or-later
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as
 * published by the Free Software Foundation, either version 3 of the
 * License, or (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { GeoAreaDto } from '@slr/geo-service-sdk';
import { useGetGeoArea } from 'hooks/geo-area/useGetGeoArea';
import { useSearchGeoArea } from 'hooks/geo-area/useSearchGeoArea';
import { useState } from 'react';
import { Col, Row } from 'react-bootstrap';
import { Control, FieldValues, Path, useController } from 'react-hook-form';
import AsyncSelect from 'react-select';

import './SLRGeoAreaForm.scss';

export type SLRGeoAreaFormProps<T extends FieldValues> = {
  id: string;
  control: Control<T>;
  name: Path<T>;
  isInvalid?: boolean;
  placeholder: string;
  notFoundLabel: string;
  loadingLabel: string;
  minSearchLabel: string;
  onSelect: (geoArea?: GeoAreaDto) => void;
};

export const SLRGeoAreaForm = <T extends FieldValues>({
  id,
  control,
  name,
  isInvalid,
  placeholder,
  notFoundLabel,
  loadingLabel,
  minSearchLabel,
  onSelect
}: SLRGeoAreaFormProps<T>) => {
  const {
    field: { value, onChange }
  } = useController({
    name,
    control
  });

  const { data: geoArea } = useGetGeoArea(value);

  const [search, setSearch] = useState<string>(geoArea?.name ?? '');

  const { data: geoAreas, isFetching } = useSearchGeoArea(search);

  return (
    <AsyncSelect
      inputId={id}
      className={`slr-geo-area-form ${isInvalid ? 'invalid' : ''}`}
      isClearable
      classNamePrefix="form"
      value={geoArea}
      isLoading={isFetching}
      options={geoAreas}
      onInputChange={(input, { action }) => {
        if (action === 'input-change') setSearch(input);
      }}
      onChange={(selected) => {
        if (selected) {
          onSelect(selected);
          onChange(selected.id);
        } else {
          setSearch('');
          onSelect(undefined);
          onChange(null);
        }
      }}
      filterOption={() => true}
      placeholder={<span>{placeholder}</span>}
      loadingMessage={() => <span>{loadingLabel}</span>}
      noOptionsMessage={() => (
        <span>{search?.length > 2 ? notFoundLabel : minSearchLabel}</span>
      )}
      formatOptionLabel={(option: GeoAreaDto & { parents?: string }) => (
        <Row>
          <Col xs="12" className="geoArea-name lh-base">
            {option.name}
          </Col>
          {option.parents && (
            <Col className="geoArea-parents lh-base">{option.parents}</Col>
          )}
        </Row>
      )}
    />
  );
};
